/* Architecture Lock panels — Executive Dashboard, Maturity, Whitepapers, Audit Mode, Lineage. */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2, Gauge, FileText, Lock, GitBranch, TrendingUp } from "lucide-react";

const inputCls = "rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cobalt/30";
const err = (e) => formatApiErrorDetail(e.response?.data?.detail?.message || e.response?.data?.detail) || "Failed";
const Spinner = () => <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;

const Stat = ({ label, value, tid, tone = "" }) => (
  <div className={`rounded-2xl bg-white border border-slate-100 p-4 ${tone}`} data-testid={tid}>
    <div className="text-[10px] font-bold uppercase text-slate-400 leading-tight">{label}</div>
    <div className="font-heading text-xl font-extrabold text-navy mt-1">{value}</div>
  </div>
);

export const ExecutiveDashboard = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/compliance/executive").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <Spinner />;
  const bandTone = { Critical: "text-red-600", Elevated: "text-amber-600", Moderate: "text-cobalt", Low: "text-emerald-600" }[d.risk_band];
  return (
    <div className="space-y-4" data-testid="cc-executive">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        <div className="rounded-2xl bg-navy text-white p-4" data-testid="exec-risk-score">
          <div className="text-[10px] font-bold uppercase text-white/50">Overall Risk Score</div>
          <div className="font-heading text-2xl font-extrabold">{d.overall_risk_score}<span className="text-sm text-white/40">/100</span></div>
          <div className={`text-xs font-bold ${bandTone}`}>{d.risk_band}</div>
        </div>
        <Stat label="SOC 2 Readiness" value={`${d.soc2_readiness_pct}%`} tid="exec-soc2" />
        <Stat label="HIPAA Readiness" value={`${d.hipaa_readiness_pct}%`} tid="exec-hipaa" />
        <Stat label="State Privacy Coverage" value={`${d.state_privacy_coverage_pct}%`} tid="exec-state" />
        <Stat label="Audit Readiness" value={`${d.audit_readiness_pct}%`} tid="exec-audit" />
        <Stat label="Open Incidents" value={d.open_incidents} tid="exec-incidents" />
        <Stat label="High Risks" value={d.high_risks} tid="exec-high-risks" />
        <Stat label="Critical Vulnerabilities" value={d.critical_vulnerabilities} tid="exec-vulns" />
        <Stat label="Evidence Items" value={d.evidence_items} tid="exec-evidence" />
        <Stat label="Unclassified Assets" value={d.data_exposure.unclassified_assets} tid="exec-exposure" />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-3xl bg-white border border-slate-100 p-5" data-testid="exec-top-risks">
          <h3 className="font-heading font-bold text-navy text-sm">Top risks</h3>
          <div className="mt-2 space-y-1.5">
            {d.top_risks.map((r, i) => (
              <div key={i} className="rounded-xl bg-slate-50 px-3 py-2 text-xs flex justify-between gap-2">
                <span className="text-slate-600">{r.description}</span>
                <span className={`font-bold whitespace-nowrap ${r.residual_risk === "high" ? "text-red-500" : "text-amber-500"}`}>{r.residual_risk}</span>
              </div>
            ))}
            {!d.top_risks.length && <p className="text-xs text-slate-400">No open risks.</p>}
          </div>
        </div>
        <div className="rounded-3xl bg-white border border-slate-100 p-5" data-testid="exec-upcoming">
          <h3 className="font-heading font-bold text-navy text-sm">Upcoming reviews (30 days)</h3>
          <div className="mt-2 space-y-1.5">
            {d.upcoming_reviews.map((u, i) => (
              <div key={i} className="rounded-xl bg-slate-50 px-3 py-2 text-xs flex justify-between gap-2">
                <span className="text-slate-600 truncate">{u.type}: {u.name}</span>
                <span className="text-slate-400 whitespace-nowrap">{u.due?.slice(0, 10)}</span>
              </div>
            ))}
            {!d.upcoming_reviews.length && <p className="text-xs text-slate-400">Nothing due in the next 30 days.</p>}
          </div>
        </div>
      </div>
      <div className="rounded-3xl bg-white border border-slate-100 p-5" data-testid="exec-trend">
        <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4 text-cobalt" /> Compliance &amp; risk trend (daily snapshots)</h3>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase font-bold text-slate-400"><th className="px-2 py-1">Day</th><th className="px-2 py-1">Risk score</th><th className="px-2 py-1">SOC 2 %</th><th className="px-2 py-1">Open incidents</th><th className="px-2 py-1">Vendor high/unrated</th><th className="px-2 py-1">Failing controls</th></tr></thead>
            <tbody>{d.trend.map((t) => (
              <tr key={t.day} className="border-t border-slate-50 text-slate-600">
                <td className="px-2 py-1.5">{t.day}</td><td className="px-2 py-1.5">{t.risk_score}</td><td className="px-2 py-1.5">{t.soc2_pct}%</td>
                <td className="px-2 py-1.5">{t.open_incidents}</td><td className="px-2 py-1.5">{t.vendor_high_or_unrated}</td><td className="px-2 py-1.5">{t.failing_controls}</td>
              </tr>))}</tbody>
          </table>
        </div>
      </div>
      <p className="text-[10px] text-slate-400">{d.note}</p>
    </div>
  );
};

const MAT_TONE = { 1: "bg-slate-100 text-slate-500", 2: "bg-amber-50 text-amber-600", 3: "bg-cobalt/10 text-cobalt", 4: "bg-emerald-50 text-emerald-600", 5: "bg-emerald-600 text-white" };

export const MaturityPanel = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/compliance/maturity").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <Spinner />;
  return (
    <div className="space-y-4" data-testid="cc-maturity">
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        <Stat label="Avg maturity" value={`${d.average_maturity}/5`} tid="mat-avg" />
        {Object.entries(d.distribution).map(([label, n], i) => <Stat key={label} label={`L${i + 1} ${label}`} value={n} tid={`mat-dist-${i + 1}`} />)}
      </div>
      <div className="rounded-3xl bg-white border border-slate-100 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100">
          <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><Gauge className="w-4 h-4 text-cobalt" /> Control maturity — implementation status + operational maturity</h3>
          <p className="text-[10px] text-slate-400 mt-0.5">{d.note}</p>
        </div>
        <div className="max-h-[520px] overflow-y-auto">
          {d.controls.map((c) => (
            <div key={c.tsc_ref} className="flex items-center gap-3 px-5 py-2.5 border-t border-slate-50 text-xs" data-testid={`mat-row-${c.tsc_ref}`}>
              <b className="text-navy w-14">{c.tsc_ref}</b>
              <span className="flex-1 text-slate-600 truncate">{c.description}</span>
              <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold whitespace-nowrap">{c.implementation_status}</span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap ${MAT_TONE[c.maturity_level]}`} title={c.maturity_basis}>L{c.maturity_level} {c.maturity_label}</span>
              <span className="text-slate-300 text-[10px] w-14 text-right">{c.evidence_count} evid.</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const WhitepapersPanel = () => {
  const [kinds, setKinds] = useState(null);
  useEffect(() => { api.get("/compliance/whitepapers").then(({ data }) => setKinds(data.kinds)).catch(() => {}); }, []);
  if (!kinds) return <Spinner />;
  const dl = async (k) => {
    try {
      const res = await api.get(`/compliance/whitepapers/${k}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `crash_ready_${k}.md`; a.click();
      URL.revokeObjectURL(url); toast.success("Generated from live system data");
    } catch { toast.error("Generation failed"); }
  };
  return (
    <div className="rounded-3xl bg-white border border-slate-100 p-6" data-testid="cc-whitepapers">
      <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><FileText className="w-4 h-4 text-cobalt" /> Whitepaper generator</h3>
      <p className="text-[11px] text-slate-400 mt-1">Every document is generated at download time from live Command Center data — never from static copy.</p>
      <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {kinds.map((k) => (
          <button key={k.key} onClick={() => dl(k.key)} data-testid={`wp-${k.key}`}
            className="text-left rounded-2xl border border-slate-200 p-4 text-xs font-semibold text-slate-600 hover:border-cobalt hover:text-cobalt transition-colors">
            {k.title}<div className="text-[10px] text-slate-300 font-normal mt-1">Markdown · live data</div>
          </button>
        ))}
      </div>
    </div>
  );
};

export const AuditModePanel = () => {
  const [d, setD] = useState(null);
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");
  const [artifact, setArtifact] = useState("");
  const [exception, setException] = useState("");
  const load = useCallback(() => api.get("/compliance/audit-mode").then(({ data }) => setD(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Spinner />;
  const on = d.audit_mode.enabled;
  const toggle = async () => {
    try {
      await api.put("/compliance/audit-mode", { enabled: !on, reason });
      toast.success(!on ? "AUDIT MODE ENABLED — evidence & controls frozen" : "Audit mode disabled"); setReason(""); load();
    } catch (e) { toast.error(err(e)); }
  };
  const post = async (path, body, ok) => {
    try { await api.post(path, body); toast.success(ok); load(); } catch (e) { toast.error(err(e)); }
  };
  const dlPackage = async () => {
    try {
      const { data } = await api.get("/compliance/audit-mode/package");
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
      const a = document.createElement("a"); a.href = url; a.download = "crash_ready_audit_package.json"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Package failed"); }
  };
  return (
    <div className="space-y-4" data-testid="cc-audit-mode">
      <div className={`rounded-3xl border p-6 ${on ? "bg-red-50 border-red-200" : "bg-white border-slate-100"}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><Lock className="w-4 h-4" /> Audit Mode: {on ? "ACTIVE" : "off"}</h3>
            <p className="text-[11px] text-slate-500 mt-1">When enabled: evidence and control records are frozen (edits require documented change approval); auditor comments, requested artifacts, exceptions and remediation are tracked.</p>
            {on && <p className="text-[11px] text-red-500 mt-1 font-semibold">Enabled by {d.audit_mode.by} on {d.audit_mode.started_at?.slice(0, 10)} — "{d.audit_mode.reason}"</p>}
          </div>
          <div className="flex gap-2 items-center">
            {!on && <input placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="am-reason" className={inputCls} />}
            <button onClick={toggle} data-testid="am-toggle" className={`px-5 py-2.5 rounded-full text-white text-xs font-bold ${on ? "bg-slate-500" : "bg-red-500"}`}>{on ? "End Audit Mode" : "Enable Audit Mode"}</button>
            <button onClick={dlPackage} data-testid="am-package" className="px-5 py-2.5 rounded-full bg-navy text-white text-xs font-bold">Generate audit package</button>
          </div>
        </div>
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        {[{ title: "Auditor comments", rows: d.comments, val: comment, set: setComment, tid: "am-comment",
            add: () => post("/compliance/audit-mode/comments", { text: comment }, "Comment recorded"),
            render: (r) => <span><b className="text-navy">{r.author}</b>: {r.text}{r.control_ref ? ` (${r.control_ref})` : ""}</span> },
          { title: "Requested artifacts", rows: d.artifacts, val: artifact, set: setArtifact, tid: "am-artifact",
            add: () => post("/compliance/audit-mode/artifacts", { description: artifact }, "Artifact request tracked"),
            render: (r) => <span>{r.description} · <b className={r.status === "fulfilled" ? "text-emerald-600" : "text-amber-600"}>{r.status}</b>{r.status !== "fulfilled" && <button onClick={async () => { await api.put(`/compliance/audit-mode/artifacts/${r.artifact_id}`); load(); }} className="ml-2 text-cobalt font-bold text-[10px]" data-testid={`am-fulfill-${r.artifact_id}`}>Fulfill</button>}</span> },
          { title: "Exceptions & remediation", rows: d.exceptions, val: exception, set: setException, tid: "am-exception",
            add: () => post("/compliance/audit-mode/exceptions", { description: exception }, "Exception raised"),
            render: (r) => <span>{r.description} · <b className={r.status === "open" ? "text-red-500" : "text-emerald-600"}>{r.status}</b>{r.status === "open" && <button onClick={async () => { await api.put(`/compliance/audit-mode/exceptions/${r.exception_id}`, { status: "remediated" }); load(); }} className="ml-2 text-cobalt font-bold text-[10px]" data-testid={`am-remediate-${r.exception_id}`}>Mark remediated</button>}</span> }]
          .map((s) => (
            <div key={s.title} className="rounded-3xl bg-white border border-slate-100 p-5">
              <h4 className="font-heading font-semibold text-navy text-sm">{s.title} ({s.rows.length})</h4>
              <div className="mt-2 flex gap-2">
                <input placeholder="Add…" value={s.val} onChange={(e) => s.set(e.target.value)} data-testid={`${s.tid}-input`} className={`${inputCls} flex-1`} />
                <button onClick={s.add} data-testid={`${s.tid}-add`} className="px-3 py-2 rounded-full bg-navy text-white text-[10px] font-bold">Add</button>
              </div>
              <div className="mt-2 space-y-1.5 max-h-56 overflow-y-auto">
                {s.rows.map((r, i) => <div key={i} className="rounded-lg bg-slate-50 px-3 py-1.5 text-[11px] text-slate-600">{s.render(r)}</div>)}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export const LineagePanel = () => {
  const [type, setType] = useState("user");
  const [id, setId] = useState("");
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const trace = async () => {
    setLoading(true);
    try { const { data } = await api.get("/compliance/lineage", { params: { entity_type: type, entity_id: id } }); setD(data); }
    catch (e) { toast.error(err(e)); } finally { setLoading(false); }
  };
  const SECTIONS = [["origin", "Origin"], ["timeline_touches", "Timeline touches"], ["access_and_exports", "Who viewed / modified / exported"],
    ["compliance_events", "Compliance events"], ["consents", "Consents"], ["vendor_transmissions", "Vendors that received it"],
    ["api_touches", "APIs that touched it"], ["ai_processes", "AI processes"], ["legal_holds", "Legal holds"],
    ["deletion_status", "Deletion status"], ["privacy_requests", "Privacy requests"], ["retention_rules", "Retention rules"],
    ["evidence_history", "Evidence history"], ["asset", "Asset record"]];
  return (
    <div className="space-y-4" data-testid="cc-lineage">
      <div className="rounded-3xl bg-white border border-slate-100 p-5">
        <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><GitBranch className="w-4 h-4 text-cobalt" /> Data lineage — composed live, never duplicated</h3>
        <div className="mt-3 flex flex-wrap gap-2">
          <select value={type} onChange={(e) => setType(e.target.value)} data-testid="lin-type" className={inputCls}>
            <option value="user">User</option><option value="case">Case</option><option value="asset">Data asset</option>
          </select>
          <input placeholder="Entity ID (user_id / case_id / asset name)" value={id} onChange={(e) => setId(e.target.value)} data-testid="lin-id" className={`${inputCls} w-80`} />
          <button onClick={trace} disabled={!id || loading} data-testid="lin-trace" className="px-5 py-2 rounded-full bg-navy text-white text-xs font-bold disabled:opacity-50">{loading ? "Tracing…" : "Trace lineage"}</button>
        </div>
      </div>
      {d && (
        <div className="grid lg:grid-cols-2 gap-3" data-testid="lin-results">
          {SECTIONS.filter(([k]) => d[k] !== undefined && d[k] !== null).map(([k, label]) => (
            <div key={k} className="rounded-3xl bg-white border border-slate-100 p-4">
              <h4 className="font-heading font-semibold text-navy text-xs">{label} {Array.isArray(d[k]) && <span className="text-slate-300">({d[k].length})</span>}</h4>
              <div className="mt-2 rounded-xl bg-slate-50 p-3 text-[10px] font-mono text-slate-600 max-h-48 overflow-y-auto whitespace-pre-wrap">
                {JSON.stringify(d[k], null, 1)}
              </div>
            </div>
          ))}
          <p className="text-[10px] text-slate-400 lg:col-span-2">{d.note}</p>
        </div>
      )}
    </div>
  );
};
